/**
 * Pure RequestInit and request-header helpers. The impure `safeRequestJson` lives in the consolidated tools
 * response-json leaf; floor-pure, value-importing only `@federa/const` plus type-only imports.
 *
 * THE URL HALF LEFT for `utils/http/url-params.ts` in this same change, and the reason is a measurement rather than
 * tidiness. `extractPathParam` and `processSearchParams` derive from no const SSOT and have no `utils/` or `const/`
 * consumer, so neither arm (a) nor arm (c) of `QUAL-CONST-FUNCTION-DISCIPLINE-001`'s fix is TRUE for them, and that
 * rule's fix ends "Never satisfy the rule by pasting the directive without a truthful reason." Arm (b) -- relocate
 * to a `utils/<topic>/` leaf and rewire every importer -- is the arm that fits, so it is the one taken. The name
 * also stops half-lying: what remains is RequestInit and header work, end to end.
 *
 * HOMED IN `const/`, NOT `utils/` (board card #150-P2), and named into the `const/http-*`
 * family beside its closest sibling `const/http-form-post-init.ts` -- the OTHER RequestInit builder, which
 * `QUAL-BEARER-JSON-HEADERS-INLINE-001` already names as a canonical declaration site at exactly that spelling. The
 * two builders now sit in one layer under one naming rule rather than straddling the floor.
 *
 * DRIVER: the "floor-pure" claim above was always the point -- every value import is `@federa/const`, so nothing
 * pinned this leaf to `utils/`. Meanwhile THREE `utils/` leaves value-import it --
 * `utils/cloudflare/authenticated-json-call.ts`, `utils/testing/json-request.ts` and
 * `utils/http/extract-token-from-bearer-header.ts` -- across a utils-to-utils edge
 * `QUAL-NO-RUNTIME-IMPORTS-IN-UTILS-001` forbids at HIGH with no allowlist.
 *
 * THE RECOGNIZER MOVED WITH THE LEAF, same commit, single specifier:
 * `QUAL-BEARER-JSON-HEADERS-INLINE-001`'s `REQUEST_INIT_CANONICAL_DECLARATION_PATH` -- the
 * `endsWithPathSegments` no-op admission for the arm whose shape this file AUTHORS -- plus both violation
 * messages and the `fix`. `QUAL-TEST-JSON-POST-REQUEST-INIT-SSOT-001` cites this path and the suite path in prose
 * only, and both cites moved too.
 */

import { buildBearerHeader } from '@federa/const/http-bearer-auth';
import { STANDARD_HEADERS } from '@federa/const/http-header-names';
import { HTTP_METHODS } from '@federa/const/http-methods';
import { CONTENT_TYPES, JSON_HEADERS, WEBSOCKET_UPGRADE_TOKEN } from '@federa/const/http-headers';
import type { HttpMethod } from '@federa/const/http-methods';

interface BuildJsonRequestInitOptions {
	keepalive?: boolean;
	extraHeaders?: Record<string, string>;
	/** Cancellation channel for a request whose caller can decide mid-flight that it no longer wants the answer (a voice
	 *  turn the caller talked over). Omitted rather than set to `undefined` when absent, so an init built without one is
	 *  byte-identical to the pre-signal shape. */
	signal?: AbortSignal;
}

/** Builds a RequestInit for any JSON-bodied HTTP method; when `method` is `GET` or `DELETE` the returned init omits the `body` field. `keepalive` is for fire-and-forget telemetry beacons that must survive page unload, `extraHeaders` merges additional headers (e.g. `Authorization`) on top of `JSON_HEADERS` where caller-provided keys win on conflict, and `signal` carries a caller-owned cancellation.
 *  @runtime-derived-ssot-helper (const-leaf-pinned + utils-floor-pinned): the header record IS the same-layer
 *  `JSON_HEADERS` SSOT (`const/http-headers.ts`) and the body-omission branch tests the same-layer `HTTP_METHODS`
 *  SSOT (`const/http-methods.ts`), so neither the headers nor the verb set is spelled here. It is also the
 *  `REQUEST_INIT_CANONICAL_DECLARATION_PATH` admission of `QUAL-BEARER-JSON-HEADERS-INLINE-001`, and
 *  `utils/cloudflare/authenticated-json-call.ts` value-imports this family, so relocation arm (b) of
 *  `QUAL-CONST-FUNCTION-DISCIPLINE-001` is foreclosed twice over. */
export function buildJsonRequestInit(method: HttpMethod, body: unknown, options: BuildJsonRequestInitOptions = {}): RequestInit {
	const { keepalive, extraHeaders, signal } = options;
	const headers = extraHeaders ? { ...JSON_HEADERS, ...extraHeaders } : JSON_HEADERS;
	const init: RequestInit = { method, headers };
	if (method !== HTTP_METHODS.GET && method !== HTTP_METHODS.DELETE) {
		init.body = JSON.stringify(body);
	}
	if (keepalive) init.keepalive = true;
	if (signal) init.signal = signal;
	return init;
}

/** JSON POST RequestInit -- thin delegate over `buildJsonRequestInit(HTTP_METHODS.POST, ...)`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned + utils-floor-pinned): the verb is the same-layer
 *  `HTTP_METHODS.POST` SSOT, never the literal, and `utils/testing/json-request.ts` value-imports this builder as
 *  the production init its `postJsonRequest` wraps -- a utils sibling it may not reach, so relocation arm (b) is
 *  foreclosed. */
export function buildJsonPostInit(body: unknown, options: BuildJsonRequestInitOptions = {}): RequestInit {
	return buildJsonRequestInit(HTTP_METHODS.POST, body, options);
}

/** JSON POST RequestInit under a caller credential: the token rides `Authorization: Bearer`, and a null one sends NO
 *  header rather than throwing, so the worker's gate stays the one authority on a signed-out caller.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-leaf `buildJsonPostInit` with the same-layer
 *  `buildBearerHeader` (`const/http-bearer-auth.ts`), so neither the verb, the header name nor the scheme is spelled
 *  here -- the one home of the credentialed-POST init the route transports and the chat upload each built inline. */
export function buildBearerJsonPostInit(body: unknown, token: string | null | undefined): RequestInit {
	return buildBearerJsonRequestInit(HTTP_METHODS.POST, body, token);
}

/** The same credentialed init for ANY verb: `buildJsonRequestInit` with the bearer header merged over the JSON
 *  headers, and no header at all for a missing token -- the Iceberg catalog reads and commits, the R2 SQL query, the
 *  Cloudflare JSON call and the work-progress emit each spelled `{ extraHeaders: buildJsonHeaders({ token }) }`.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): composes the same-leaf `buildJsonRequestInit` with the same-layer
 *  `buildBearerHeader`, so neither the header name nor the scheme is spelled here. */
export function buildBearerJsonRequestInit(method: HttpMethod, body: unknown, token: string | null | undefined): RequestInit {
	return buildJsonRequestInit(method, body, { extraHeaders: buildBearerHeader(token) });
}

/** Bodiless GET RequestInit asking for JSON back (`Accept`, not the `Content-Type` of `JSON_HEADERS`): the read the
 *  places autocomplete and geocode calls and the chats inline property card each spelled inline. A fresh record per
 *  call, so no caller shares a mutable headers object.
 *  @runtime-derived-ssot-helper (const-leaf-pinned): the verb, the header name and the media type are the same-layer
 *  `HTTP_METHODS`, `STANDARD_HEADERS` and `CONTENT_TYPES` SSOTs, so none is spelled here. */
export function buildAcceptJsonGetInit(): RequestInit {
	return { method: HTTP_METHODS.GET, headers: { [STANDARD_HEADERS.ACCEPT]: CONTENT_TYPES.JSON } };
}

/**
 * Does this request carry a WebSocket upgrade?
 *
 * THE COMPARE IS CASE-INSENSITIVE BECAUSE THE HEADER VALUE IS. `Upgrade: WebSocket` is exactly as valid as
 * `Upgrade: websocket` (RFC 9110 protocol tokens are case-insensitive), and a strict `===` refused it -- silently, at
 * every consumer, since each one answers a bare 401 or falls through to the HTTP leg rather than reporting a mismatch.
 * That is not hypothetical on the voice lane: ConnectCallDO gates the ConversationRelay socket on this predicate, so a
 * proxy that normalized the casing differently would have hung up on every inbound AI call with nothing logged.
 *
 * AND IT IS THE ONE MATCHER for this question repo-wide. Three surfaces carried a PRIVATE lowercase-normalizing copy --
 * both host WS proxies (`apps/federa/src/worker-ws-proxy.ts`, `apps/admin-federa/src/worker-ws-proxy.ts`) and
 * `buildRoomRequest` (`tools/conversations/build-room-request.ts`) -- and each now calls this instead. That matters
 * beyond tidiness: while the copies existed, the casing rule could hold at a proxy and regress at the predicate behind
 * it (which is the state the fleet was actually in), and a reader auditing one surface learned nothing about the others.
 * Compared against `WEBSOCKET_UPGRADE_TOKEN` (`const/http-headers.ts`), the same SSOT the producer side SETS the header
 * from, so reader and writer cannot drift.
 *
 * @runtime-derived-ssot-helper (const-leaf-pinned): BOTH sides of the compare are same-layer SSOTs -- the header
 * name is `STANDARD_HEADERS.UPGRADE` (`const/http-header-names.ts`) and the expected value is
 * `WEBSOCKET_UPGRADE_TOKEN` (`const/http-headers.ts`), which is exactly why reader and writer cannot drift.
 */
export function isWebSocketRequest(request: Request): boolean {
	return request.headers.get(STANDARD_HEADERS.UPGRADE)?.toLowerCase() === WEBSOCKET_UPGRADE_TOKEN;
}
